import { mapHelper } from "../../utils/maps/map-helper";
import { OverlayHelper } from "./OverlayHelper";
import { SvgElementBuilder } from "./SvgElementBuilder";

const styleSheet: string = `
  path {
    fill: #A1A1A1;
    transition: fill 0.25s ease-in-out;
  }
  .highlighted {
    fill: white;
    transition: fill 0.25s ease-in-out;
  }
`;

// Apply an arbitrary scale to the SVGs aside from the CMS value.
const svgDocStyles = { transform: "scale(0.5)" };

interface ParcelState {
  selected: boolean;
  over: boolean;
  focused: boolean;
  selectedViaKeyboard: boolean;
}

export class Parcel {
  state: ParcelState;
  markerOverlay: any;
  readonly map: google.maps.Map;
  readonly feature: google.maps.Data.Feature;
  private svgBuilder?: SvgElementBuilder;

  private constructor(feature: google.maps.Data.Feature, map: google.maps.Map) {
    this.feature = feature;
    this.map = map;
    this.state = {
      selected: false,
      over: false,
      focused: false,
      selectedViaKeyboard: false,
    };
    this.highlighted = false;
  }

  static async create(feature: google.maps.Data.Feature, map: google.maps.Map): Promise<Parcel> {
    const parcel = new Parcel(feature, map);
    parcel.markerOverlay = await parcel.createOverlay();
    return parcel;
  }

  private async createOverlay(): Promise<any> {
    const logo = this.feature.getProperty("logo") as any;
    if (!logo) return null;

    const svgBuilder = new SvgElementBuilder();
    await svgBuilder.load(logo.url, styleSheet, svgDocStyles).then(() => {});

    let title = this.feature.getProperty("title") as string;
    title = decodeHTMLEntities(title);

    // make objectElement tabbable
    svgBuilder.objectElement?.setAttribute("tabindex", "0");
    svgBuilder.objectElement?.setAttribute("role", "img");
    svgBuilder.objectElement?.setAttribute("aria-label", title);
    svgBuilder.objectElement?.addEventListener("focus", () => {
      this.handleFocused();
    });
    svgBuilder.objectElement?.addEventListener("blur", () => {
      this.handleUnfocused();
    });
    svgBuilder.objectElement?.addEventListener("keydown", (event) => {
      if (event.key === "Enter") {
        this.setAsActivated({ viaKeyboard: true });
        event.preventDefault();
      }
    });
    this.svgBuilder = svgBuilder;

    this.feature.setProperty("svgBuilder", svgBuilder);

    const featureBounds = mapHelper.getFeatureBounds(this.feature);
    if (!featureBounds) return null;

    const overlay = await OverlayHelper.createPinnedOverlay({
      element: svgBuilder.containerElement,
      map: this.map,
      bounds: featureBounds,
      scale: logo.scale,
      x: logo.x,
      y: logo.y,
    });
    return overlay;
  }

  get interactiveElement(): HTMLElement | null {
    return this.svgBuilder?.objectElement || null;
  }

  get id(): string {
    return this.feature.getProperty("id") as string;
  }

  set selected(value: boolean) {
    this.state.selected = value;
  }
  get selected(): boolean {
    return this.state.selected;
  }

  set over(value: boolean) {
    this.state.over = value;
  }
  get over(): boolean {
    return this.state.over;
  }

  set focused(value: boolean) {
    this.state.focused = value;
  }
  get focused(): boolean {
    return this.state.focused;
  }

  set selectedViaKeyboard(value: boolean) {
    this.state.selectedViaKeyboard = value;
  }
  get selectedViaKeyboard(): boolean {
    return this.state.selectedViaKeyboard;
  }

  set highlighted(highlighted: boolean) {
    if (highlighted) {
      if (!this.selected) {
        this.map.data.overrideStyle(this.feature, { fillColor: "#47baa3" });
      }
    } else {
      this.map.data.revertStyle(this.feature);
    }

    const builderSVG = this.feature.getProperty("svgBuilder") as any;
    if (builderSVG?.svg) {
      const paths = builderSVG.svg.querySelectorAll("path") as NodeListOf<HTMLElement>;
      paths.forEach((path: HTMLElement) => path.classList.toggle("highlighted", highlighted));
    }
  }

  get Fg(): any {
    return (this.feature as any).Fg;
  }

  get interactive(): boolean {
    const builder = this.feature.getProperty("builderId");
    //console.log("INTERACTIVE? : ", builder);
    return builder ? true : false;
  }

  setAsActivated(params: { viaKeyboard: boolean }): void {
    this.selected = true;
    this.selectedViaKeyboard = params.viaKeyboard;
    this.highlighted = true;
    this.over = false;

    this.map.data.overrideStyle(this.feature, { fillColor: "#00A7FF" });
  }

  handleFocused(): void {
    this.focused = true;
    this.highlighted = true;
  }

  handleUnfocused(): void {
    this.focused = false;
    if (!this.selected) this.highlighted = false;
  }

  handleMouseOver(): void {
    this.over = true;
    this.highlighted = true;
  }

  handleMouseOut(): void {
    this.over = false;
    if (!this.selected) this.highlighted = false;
  }

  unactivate(): void {
    // Used by the map when selecting a new parcel,
    // and having to unselect all others.
    this.selected = false;
    if (!this.over) this.highlighted = false;
  }
}

function decodeHTMLEntities(value: string) {
  const txt = document.createElement("textarea");
  txt.innerHTML = value;
  return txt.value;
}